import React from 'react';
import { Link } from 'react-router-dom';
import PublicImage from '../PublicImage';
import { cartLineKey } from '../cart/cartUtils';
import OrderCustomizationDisplay from '../jewelry/OrderCustomizationDisplay';
import { formatINR } from './formatINR';
import { useSiteSettings } from '../SiteSettingsContext';
import { getMergedStoreCopy } from '../storeCopy';

function shippingLabel(shipping, loading, freeLabel) {
  if (loading) return '…';
  if (shipping > 0) return formatINR(shipping);
  return freeLabel;
}

export default function OrderSummaryPanel({
  lines,
  subtotal,
  discount = 0,
  couponCode = '',
  freeShippingFromCoupon = false,
  shipping = 0,
  shippingCaption = '',
  shippingLoading = false,
  showThumbnails = true,
  editCartHref = '/cart',
  primaryAction,
  footnote,
  deliveryNote,
}) {
  const { settings: site } = useSiteSettings();
  const store = getMergedStoreCopy(site);
  const total = Math.max(0, subtotal - discount) + (shippingLoading ? 0 : shipping);
  const itemCount = lines.reduce((s, l) => s + l.qty, 0);
  const countLabel =
    itemCount === 1
      ? `${itemCount} ${store.cart_item_singular}`
      : `${itemCount} ${store.cart_item_plural}`;

  return (
    <aside className="order-summary-panel">
      <div className="order-summary-panel__head">
        <h2 className="order-summary-panel__title">{store.order_summary_title}</h2>
        <span className="order-summary-panel__count">{countLabel}</span>
      </div>

      {deliveryNote ? <p className="order-summary-panel__delivery-note small mb-3">{deliveryNote}</p> : null}

      <ul className="order-summary-panel__lines list-unstyled">
        {lines.map((line) => (
          <li
            key={cartLineKey(line.categorySlug, line.productId, line.customization)}
            className="order-summary-panel__line"
          >
            {showThumbnails ? (
              <div className="order-summary-panel__thumb">
                {line.image ? (
                  <PublicImage src={line.image} alt="" />
                ) : (
                  <span className="order-summary-panel__thumb-placeholder" aria-hidden />
                )}
                <span className="order-summary-panel__qty-badge">{line.qty}</span>
              </div>
            ) : null}
            <div className="order-summary-panel__line-body">
              <span className="order-summary-panel__name">{line.name}</span>
              {line.customization ? (
                <span className="order-summary-panel__meta d-block">
                  <OrderCustomizationDisplay raw={line.customization} product={line.product} />
                </span>
              ) : null}
              {!showThumbnails ? (
                <span className="order-summary-panel__meta">
                  {store.order_summary_qty} {line.qty}
                </span>
              ) : null}
            </div>
            <span className="order-summary-panel__line-price">{formatINR(line.price * line.qty)}</span>
          </li>
        ))}
      </ul>

      <div className="order-summary-panel__rows">
        <div className="order-summary-panel__row">
          <span>{store.order_summary_subtotal}</span>
          <span>{formatINR(subtotal)}</span>
        </div>
        {discount > 0 || freeShippingFromCoupon ? (
          <div className="order-summary-panel__row order-summary-panel__row--discount">
            <span>
              {couponCode ? (
                <>
                  {store.order_summary_promo}{' '}
                  <span className="order-summary-panel__coupon-tag">{couponCode}</span>
                </>
              ) : (
                store.order_summary_promo
              )}
            </span>
            <span className="order-summary-panel__discount-val">
              {discount > 0 ? `−${formatINR(discount)}` : '—'}
              {freeShippingFromCoupon ? (
                <span className="order-summary-panel__free-ship-note"> + free delivery</span>
              ) : null}
            </span>
          </div>
        ) : null}
        <div className="order-summary-panel__row">
          <span>{shippingCaption ? `${store.order_summary_delivery} — ${shippingCaption}` : store.order_summary_delivery}</span>
          <span>{shippingLabel(shipping, shippingLoading, store.order_summary_delivery_free)}</span>
        </div>
        <div className="order-summary-panel__row order-summary-panel__row--total">
          <span>{store.order_summary_total}</span>
          <span>{shippingLoading ? formatINR(subtotal) : formatINR(total)}</span>
        </div>
      </div>

      {primaryAction ? <div className="order-summary-panel__action">{primaryAction}</div> : null}

      {editCartHref ? (
        <Link to={editCartHref} className="order-summary-panel__edit">
          {store.order_summary_edit_bag}
        </Link>
      ) : null}

      {footnote ? <p className="order-summary-panel__footnote small text-muted mb-0">{footnote}</p> : null}
    </aside>
  );
}
